import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, ChevronDown, Bell, Mail } from "lucide-react";

import FacebookStyleLoader from "../../ChildComponents/Loading";
import SubNav from "../../ChildComponents/SubNav";
import profileIcon from "../../image/profile.png";
import { logout } from "../../store/actions/userActions";

function Header({ data }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeMobile, setActiveMobile] = useState(false);
  const [megaNav, setMegaNav] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const containerRef = useRef(null);
  const megaNavRef = useRef(null);

  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const mobileNav = () => setActiveMobile(!activeMobile);

  const handleMouseDown = (e) => {
    setIsMouseDown(true);
    setStartX(e.clientX);
    setScrollLeft(containerRef.current.scrollLeft);
    containerRef.current.style.cursor = "grabbing";
  };

  const handleMouseMove = (e) => {
    if (!isMouseDown) return;
    const x = e.clientX - startX;
    containerRef.current.scrollLeft = scrollLeft - x;
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
    containerRef.current.style.cursor = "grab";
  };

  const handleMouseLeave = () => {
    if (isMouseDown) {
      setIsMouseDown(false);
      containerRef.current.style.cursor = "grab";
    }
  };

  const openMegaNav = () => {
    setMegaNav(!megaNav);
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close megaNav on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        megaNavRef.current &&
        !megaNavRef.current.contains(event.target) &&
        !event.target.classList.contains("sub-mega-nav-point")
      ) {
        setMegaNav(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const logOutHandler = (e) => {
    e.preventDefault();
    dispatch(logout());
    setLoading(false);
    navigate("/login");
  };

  if (loading) {
    return <FacebookStyleLoader />;
  }

  return (
    <>
      {/* Main nav */}
      <div
        className={`bg-white sticky-top transition-all ${
          scrolled ? "shadow-lg" : "shadow-sm"
        }`}
        style={{
          transition: "all 0.3s ease",
          borderBottom: scrolled ? "2px solid #f59e0b" : "1px solid #e5e7eb",
        }}
      >
        <div className="container-fluid">
          <div className="row align-items-center py-2">
            {/* Logo Section */}
            <div className="col-auto d-flex align-items-center">
              <button
                className="btn btn-link text-decoration-none d-lg-none p-0 me-3"
                onClick={mobileNav}
                style={{ color: "#f59e0b" }}
              >
                <Menu size={28} />
              </button>
              <div className="d-flex align-items-center">
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center me-2"
                  style={{
                    width: "40px",
                    height: "40px",
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                  }}
                >
                  <span className="text-white fw-bold fs-5">N</span>
                </div>
                <div>
                  <h5
                    className="mb-0 fw-bold"
                    style={{
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontSize: "1.1rem",
                    }}
                  >
                    News Admin
                  </h5>
                  <p className="mb-0 text-muted" style={{ fontSize: "0.7rem" }}>
                    Dashboard
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="col d-none d-lg-block">
              <nav className="navbar navbar-expand-lg p-0">
                <ul className="navbar-nav mx-auto mb-0">
                  {data.map((item, index) => {
                    const isActive = location.pathname.startsWith(item.url);
                    return (
                      <li key={index} className="nav-item mx-1">
                        <Link
                          className={`nav-link d-flex align-items-center px-3 py-2 rounded-3 position-relative`}
                          to={item.url}
                          style={{
                            transition: "all 0.3s ease",
                            background: isActive
                              ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                              : "transparent",
                            color: isActive ? "white" : "#6b7280",
                            fontWeight: isActive ? "600" : "500",
                            fontSize: "0.9rem",
                            transform: isActive ? "translateY(-2px)" : "none",
                            boxShadow: isActive
                              ? "0 4px 12px rgba(245, 158, 11, 0.3)"
                              : "none",
                          }}
                        >
                          {item.icon && (
                            <span
                              className="material-symbols-outlined me-2"
                              style={{ fontSize: "1.2rem" }}
                            >
                              {item.icon}
                            </span>
                          )}
                          {item.label && (
                            <span className="text-nowrap">{item.label}</span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>

            {/* Right Section - Actions */}
            <div className="col-auto d-flex align-items-center">
              {/* Notifications & Messages - Hidden on mobile */}
              {/* <div className="d-none d-md-flex align-items-center me-3">
                <button
                  className="btn btn-link p-2 position-relative"
                  style={{ color: "#6b7280" }}
                >
                  <Bell size={20} />
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: "0.65rem" }}
                  >
                    3
                  </span>
                </button>
                <button
                  className="btn btn-link p-2 position-relative"
                  style={{ color: "#6b7280" }}
                >
                  <Mail size={20} />
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning"
                    style={{ fontSize: "0.65rem" }}
                  >
                    5
                  </span>
                </button>
              </div> */}

              {/* Profile Dropdown */}
              <div className="position-relative">
                <div
                  className="d-flex align-items-center cursor-pointer p-2 rounded-3 sub-mega-nav-point"
                  onClick={openMegaNav}
                  style={{
                    transition: "all 0.3s ease",
                    background: megaNav ? "#f9fafb" : "transparent",
                  }}
                >
                  <div
                    className="rounded-circle overflow-hidden me-2 border border-2"
                    style={{
                      width: "38px",
                      height: "38px",
                      borderColor: "#f59e0b",
                    }}
                  >
                    <img
                      className="img-fluid w-100 h-100 object-fit-cover"
                      src={profileIcon}
                      alt="Profile"
                    />
                  </div>
                  <ChevronDown
                    size={18}
                    className="d-none d-md-block"
                    style={{
                      color: "#6b7280",
                      transform: megaNav ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </div>

                {/* Dropdown Menu */}
                {megaNav && (
                  <div
                    className="position-absolute bg-white rounded-3 mt-2 end-0"
                    ref={megaNavRef}
                    style={{
                      minWidth: "220px",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                      zIndex: 1050,
                      animation: "slideDown 0.3s ease",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div
                      className="p-3 border-bottom"
                      style={{
                        background:
                          "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle overflow-hidden me-3 border border-2"
                          style={{
                            width: "50px",
                            height: "50px",
                            borderColor: "#f59e0b",
                          }}
                        >
                          <img
                            className="img-fluid w-100 h-100 object-fit-cover"
                            src={profileIcon}
                            alt="Profile"
                          />
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold text-dark">Admin User</h6>
                          <p className="mb-0 text-muted small">
                            admin@example.com
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-2">
                      <Link
                        className="dropdown-item d-flex align-items-center px-3 py-2 rounded-2"
                        to="/profile"
                        style={{
                          transition: "all 0.2s ease",
                          fontSize: "0.9rem",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.background = "#f9fafb")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.background = "transparent")
                        }
                      >
                        <User
                          size={18}
                          className="me-3"
                          style={{ color: "#f59e0b" }}
                        />
                        <span>View Profile</span>
                      </Link>

                      <hr className="my-2" style={{ opacity: 0.1 }} />

                      <Link
                        className="dropdown-item d-flex align-items-center px-3 py-2 rounded-2 text-danger fw-semibold"
                        to="/"
                        onClick={logOutHandler}
                        style={{
                          transition: "all 0.2s ease",
                          fontSize: "0.9rem",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.background = "#fef2f2")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.background = "transparent")
                        }
                      >
                        <LogOut size={18} className="me-3" />
                        <span>Logout</span>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Sidebar */}
        <div
          className={`position-fixed top-0 start-0 w-100 h-100 ${
            activeMobile ? "d-block" : "d-none"
          }`}
          onClick={mobileNav}
          style={{
            background: "rgba(0,0,0,0.5)",
            zIndex: 1060,
            backdropFilter: "blur(4px)",
            transition: "all 0.3s ease",
          }}
        >
          <div
            className="bg-white h-100 p-0 overflow-auto"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "280px",
              boxShadow: "4px 0 24px rgba(0,0,0,0.15)",
              animation: activeMobile ? "slideInLeft 0.3s ease" : "none",
            }}
          >
            {/* Mobile Header */}
            <div
              className="p-4 border-bottom"
              style={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center me-2 bg-white"
                    style={{
                      width: "40px",
                      height: "40px",
                    }}
                  >
                    <span className="fw-bold fs-5" style={{ color: "#f59e0b" }}>
                      N
                    </span>
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold text-white">News Admin</h6>
                    <p
                      className="mb-0 text-white-50"
                      style={{ fontSize: "0.75rem" }}
                    >
                      Dashboard
                    </p>
                  </div>
                </div>
                <button
                  className="btn btn-link p-0 text-white"
                  onClick={mobileNav}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Mobile Menu Items */}
            <div className="p-3">
              <nav className="navbar p-0">
                <ul className="navbar-nav w-100">
                  {data.map((item, index) => {
                    const isActive = location.pathname.startsWith(item.url);
                    return (
                      <li
                        key={index}
                        className="nav-item mb-1"
                        onClick={mobileNav}
                      >
                        <Link
                          className={`nav-link d-flex align-items-center px-3 py-3 rounded-3`}
                          to={item.url}
                          style={{
                            transition: "all 0.3s ease",
                            background: isActive
                              ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
                              : "transparent",
                            color: isActive ? "#92400e" : "#6b7280",
                            fontWeight: isActive ? "600" : "500",
                            borderLeft: isActive
                              ? "4px solid #f59e0b"
                              : "4px solid transparent",
                          }}
                        >
                          {item.icon && (
                            <span
                              className="material-symbols-outlined me-3"
                              style={{ fontSize: "1.3rem" }}
                            >
                              {item.icon}
                            </span>
                          )}
                          <span className="flex-grow-1">{item.label}</span>
                          {isActive && (
                            <span
                              className="material-symbols-outlined"
                              style={{ color: "#f59e0b" }}
                            >
                              arrow_forward_ios
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>

            {/* Mobile Footer */}
            <div
              className="p-4 border-top mt-auto"
              style={{ background: "#f9fafb" }}
            >
              <div className="d-flex align-items-center mb-3">
                <div
                  className="rounded-circle overflow-hidden me-3 border border-2"
                  style={{
                    width: "50px",
                    height: "50px",
                    borderColor: "#f59e0b",
                  }}
                >
                  <img
                    className="img-fluid w-100 h-100 object-fit-cover"
                    src={profileIcon}
                    alt="Profile"
                  />
                </div>
                <div>
                  <h6 className="mb-0 fw-bold">Admin User</h6>
                  <p className="mb-0 text-muted small">admin@example.com</p>
                </div>
              </div>
              <button
                className="btn w-100 text-danger fw-semibold d-flex align-items-center justify-content-center py-2 rounded-3"
                onClick={logOutHandler}
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#fee2e2")}
                onMouseLeave={(e) => (e.target.style.background = "#fef2f2")}
              >
                <LogOut size={18} className="me-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sub nav */}
      {data.map((item, index) => {
        const isActive = location.pathname.startsWith(item.url);
        return (
          <div key={index}>
            {isActive && item.subNav && item.subNav.length > 0 && (
              <div className="bg-white shadow-sm py-3 border-bottom">
                <div
                  ref={containerRef}
                  className="container-fluid"
                  style={{
                    overflowX: "auto",
                    cursor: "grab",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                >
                  <div
                    className="d-flex gap-2"
                    style={{ minWidth: "max-content" }}
                  >
                    {item.subNav.map((subItem, subIndex) => (
                      <SubNav
                        key={subIndex}
                        url={subItem.url}
                        icon={subItem.icon}
                        title={subItem.label}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }

        .cursor-pointer {
          cursor: pointer;
        }

        .dropdown-item:hover {
          background-color: #f9fafb;
        }

        /* Hide scrollbar */
        [style*="overflowX: auto"]::-webkit-scrollbar {
          display: none;
        }

        .nav-link {
          white-space: nowrap;
        }

        .nav-link:hover:not(.active) {
          background-color: #f9fafb !important;
          transform: translateY(-2px);
        }

        @media (max-width: 991.98px) {
          .col-auto:first-child {
            flex: 0 0 auto;
          }
        }
      `}</style>
    </>
  );
}

export default Header;
